import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Save, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SiteContentManager() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/settings")
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  if (!form) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const setHour = (i, key, val) =>
    setForm((p) => ({
      ...p,
      hours: p.hours.map((h, idx) => (idx === i ? { ...h, [key]: val } : h)),
    }));

  const addHour = () =>
    setForm((p) => ({ ...p, hours: [...p.hours, { day: "", time: "" }] }));

  const removeHour = (i) =>
    setForm((p) => ({ ...p, hours: p.hours.filter((_, idx) => idx !== i) }));

  const setMarquee = (i, val) =>
    setForm((p) => ({
      ...p,
      marquee_items: p.marquee_items.map((m, idx) => (idx === i ? val : m)),
    }));

  const addMarquee = () =>
    setForm((p) => ({ ...p, marquee_items: [...p.marquee_items, ""] }));

  const removeMarquee = (i) =>
    setForm((p) => ({
      ...p,
      marquee_items: p.marquee_items.filter((_, idx) => idx !== i),
    }));

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = {
        ...form,
        hours: form.hours.filter((h) => h.day.trim() && h.time.trim()),
        marquee_items: form.marquee_items.map((m) => m.trim()).filter(Boolean),
      };
      const { data } = await adminApi.patch("/admin/settings", clean);
      setForm(data);
      toast.success("Saved. Live on the site immediately.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} data-testid="site-content-form" className="max-w-3xl space-y-10">
      <p className="text-sm text-[var(--trio-muted)] max-w-xl">
        Everything here goes live on the homepage the moment you hit Save.
        Refresh the site to see it.
      </p>

      <Section title="Contact">
        <Row label="Address" value={form.address} onChange={(v) => set("address", v)} testid="content-address" />
        <Row label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="content-phone" />
      </Section>

      <Section title="Hours" hint="Each row: what it is (e.g. 'Lunch · Tue – Fri') and the time (e.g. '11am – 3pm').">
        {form.hours.map((h, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`hours-row-${i}`}>
            <Input
              value={h.day}
              onChange={(e) => setHour(i, "day", e.target.value)}
              placeholder="Label (Lunch · Tue – Fri)"
              className="col-span-6 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
            />
            <Input
              value={h.time}
              onChange={(e) => setHour(i, "time", e.target.value)}
              placeholder="11am – 3pm"
              className="col-span-5 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
            />
            <button
              type="button"
              onClick={() => removeHour(i)}
              data-testid={`hours-remove-${i}`}
              className="col-span-1 border border-[var(--trio-border)] hover:border-red-500 hover:text-red-500"
            >
              <X className="w-4 h-4 mx-auto" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addHour}
          data-testid="hours-add"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--trio-primary)] hover:text-[var(--trio-primary-hover)]"
        >
          <Plus className="w-3.5 h-3.5" /> Add row
        </button>
      </Section>

      <Section title="Marquee Ticker" hint="The scrolling strip on the homepage. Keep each item short and punchy.">
        {form.marquee_items.map((m, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`marquee-row-${i}`}>
            <Input
              value={m}
              onChange={(e) => setMarquee(i, e.target.value)}
              className="col-span-11 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
            />
            <button
              type="button"
              onClick={() => removeMarquee(i)}
              data-testid={`marquee-remove-${i}`}
              className="col-span-1 border border-[var(--trio-border)] hover:border-red-500 hover:text-red-500"
            >
              <X className="w-4 h-4 mx-auto" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addMarquee}
          data-testid="marquee-add"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--trio-primary)] hover:text-[var(--trio-primary-hover)]"
        >
          <Plus className="w-3.5 h-3.5" /> Add item
        </button>
      </Section>

      <Section title="Hero Subheadline" hint="The paragraph under 'EAT. DRINK. VIBE.' on the homepage.">
        <Textarea
          value={form.hero_subheadline}
          onChange={(e) => set("hero_subheadline", e.target.value)}
          rows={4}
          data-testid="content-hero-sub"
          className="rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
        />
      </Section>

      <Section title="Group / Birthday CTA">
        <Row label="Overline" value={form.group_cta_overline} onChange={(v) => set("group_cta_overline", v)} testid="content-cta-overline" />
        <Row label="Headline" value={form.group_cta_headline} onChange={(v) => set("group_cta_headline", v)} testid="content-cta-headline" />
        <div>
          <Label className="text-xs tracking-widest uppercase">Body</Label>
          <Textarea
            value={form.group_cta_body}
            onChange={(e) => set("group_cta_body", e.target.value)}
            rows={3}
            data-testid="content-cta-body"
            className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
          />
        </div>
      </Section>

      <Section title="Footer">
        <Row label="Tagline" value={form.footer_tagline} onChange={(v) => set("footer_tagline", v)} testid="content-footer-tag" />
      </Section>

      <Section title="Staff Referral Commissions" hint="Commission paid to staff whose referral link a customer used. Event ticket commissions are set per event in the Events tab.">
        <div>
          <label className="text-xs tracking-widest uppercase text-[var(--trio-muted)]">
            Flat $ per referred reservation
          </label>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-lg">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.reservation_commission_amount ?? 0}
              onChange={(e) => set("reservation_commission_amount", Number(e.target.value))}
              data-testid="content-reservation-commission"
              className="w-32 border border-[var(--trio-border)] bg-[var(--trio-bg)] px-3 py-2 text-sm rounded-none"
            />
            <span className="text-xs text-[var(--trio-muted)]">per confirmed reservation</span>
          </div>
        </div>
      </Section>

      <Section title="Staff Sign-Up Code" hint="New hires enter this code at /staff/signup to create their own account. Change it any time — anyone who has the old code loses access.">
        <label className="inline-flex items-center gap-2">
          <Switch
            checked={form.staff_signups_open !== false}
            onCheckedChange={(v) => set("staff_signups_open", v)}
            data-testid="content-staff-signups-open"
          />
          <span className="text-xs uppercase tracking-widest font-bold">
            Accept New Sign-Ups
          </span>
        </label>
        {form.staff_signups_open === false && (
          <div className="mt-2 text-xs text-[var(--trio-primary)]">
            🚫 Sign-ups are currently frozen. Nobody can create a new staff account until you flip this back on.
          </div>
        )}
        <div className={form.staff_signups_open === false ? "opacity-40 pointer-events-none" : ""}>
          <label className="text-xs tracking-widest uppercase text-[var(--trio-muted)]">
            Current invite code
          </label>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <input
              value={form.staff_invite_code || ""}
              onChange={(e) => set("staff_invite_code", e.target.value.toUpperCase())}
              data-testid="content-staff-invite-code"
              placeholder="TRIOCREW"
              className="w-48 border border-[var(--trio-border)] bg-[var(--trio-bg)] px-3 py-2 text-sm rounded-none font-mono tracking-widest uppercase"
            />
            <button
              type="button"
              onClick={() => {
                const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
                set("staff_invite_code", `TRIO${rand}`);
              }}
              data-testid="content-invite-code-regen"
              className="text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-3 py-2"
            >
              Generate New
            </button>
            <span className="text-xs text-[var(--trio-muted)]">
              Share verbally or by text with new hires.
            </span>
          </div>
        </div>
      </Section>

      {isOwner && (
        <Section title="Maintenance Mode" hint="Owner only. When ON, visitors see a 'we'll be right back' page. Admin dashboard stays reachable.">
          <div
            data-testid="maintenance-toggle-row"
            className={`flex items-start gap-4 border p-4 ${
              form.maintenance_mode
                ? "border-red-500 bg-red-500/5"
                : "border-[var(--trio-border)]"
            }`}
          >
            <AlertTriangle
              className={`w-5 h-5 mt-0.5 ${
                form.maintenance_mode ? "text-red-500" : "text-[var(--trio-muted)]"
              }`}
            />
            <div className="flex-1">
              <div className="font-bold text-sm">
                {form.maintenance_mode ? "Site is currently OFFLINE" : "Site is live"}
              </div>
              <div className="text-xs text-[var(--trio-muted)] mt-1">
                Toggle this on while you're doing big updates. Remember to save.
              </div>
            </div>
            <Switch
              checked={!!form.maintenance_mode}
              onCheckedChange={(v) => set("maintenance_mode", v)}
              data-testid="maintenance-toggle"
            />
          </div>
        </Section>
      )}

      <div className="sticky bottom-0 -mx-8 px-8 py-4 bg-[var(--trio-bg)] border-t border-[var(--trio-border)]">
        <button
          type="submit"
          disabled={saving}
          data-testid="content-save"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-8 py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="space-y-3">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
          {title}
        </div>
        {hint && (
          <div className="text-xs text-[var(--trio-muted)] mt-1">{hint}</div>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}
